import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Tesseract from 'tesseract.js';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onResult: (data: Record<string, string>) => void;
}

type Phase = 'upload' | 'scanning' | 'review';

interface ParsedCard {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  mobile: string;
  position: string;
  companyName: string;
}

function parseCardText(text: string): ParsedCard {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);

  // E-Mail
  const emailMatch = text.match(/[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/);
  const email = emailMatch?.[0] ?? '';

  // Telefon — sucht nach "Tel", "Fon", "T:", oder reiner Nummer
  const telMatch = text.match(/(?:Tel(?:efon)?|Fon|Phone|T)\s*[.:\-]?\s*([\+\d][\d\s\-\/\(\)\.]{6,})/i);
  const phone = telMatch?.[1]?.replace(/\s{2,}/g, ' ').trim() ?? '';

  // Mobil
  const mobMatch = text.match(/(?:Mob(?:il|ile)?|Handy|Cell|M)\s*[.:\-]?\s*([\+\d][\d\s\-\/\(\)\.]{6,})/i);
  const mobile = mobMatch?.[1]?.replace(/\s{2,}/g, ' ').trim() ?? '';

  // Website (für spätere Nutzung, nicht im Prefill benötigt)

  // Firma — Suche nach Rechtsformen
  const companyMatch = text.match(/([^\n]*(?:GmbH|AG|KG|UG|GbR|OHG|SE|e\.V\.|Ltd|LLC|Corp|Inc)[^\n]*)/i);
  const companyName = companyMatch?.[1]?.trim() ?? '';

  // Position — typische Job-Bezeichnungen oder Zeilen ohne Zahlen/E-Mail
  const positionKeywords = /(?:CEO|CTO|COO|CFO|Geschäfts(?:führ|leiter)|Manager|Berater|Consultant|Direktor|Director|Leiter|Head|Lead|Senior|Junior|Engineer|Entwickler|Vertriebs|Sales|Account|Partner|Inhaber|Founder)/i;
  const positionLine = lines.find(
    (l) =>
      positionKeywords.test(l) &&
      !l.includes('@') &&
      l.length < 80,
  );
  const position = positionLine ?? '';

  // Name — erste Zeile die kein E-Mail, keine Firma, keine Zahl enthält
  const nameCandidates = lines.filter(
    (l) =>
      !l.includes('@') &&
      !/\d{3,}/.test(l) &&
      l.length > 2 &&
      l.length < 50 &&
      /^[A-ZÜÄÖA-Z]/.test(l) &&
      l !== companyName &&
      l !== position,
  );

  let firstName = '';
  let lastName = '';
  if (nameCandidates.length > 0) {
    const parts = nameCandidates[0].split(/\s+/).filter(Boolean);
    // Titel überspringen
    const titlesRx = /^(?:Dr|Prof|Dipl|Ing|M\.Sc|B\.Sc|MBA|HR)\b\.?$/i;
    const realParts = parts.filter((p) => !titlesRx.test(p));
    firstName = realParts[0] ?? '';
    lastName = realParts.slice(1).join(' ') ?? '';
  }

  return { firstName, lastName, email, phone, mobile, position, companyName };
}

const LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: '0.25rem',
};

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.9375rem',
  outline: 'none',
  boxSizing: 'border-box',
  background: 'var(--color-bg-card)',
  color: 'var(--color-text)',
};

export const BusinessCardScanner = ({ isOpen, onClose, onResult }: Props) => {
  const [phase, setPhase] = useState<Phase>('upload');
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [parsed, setParsed] = useState<ParsedCard>({
    firstName: '', lastName: '', email: '', phone: '', mobile: '', position: '', companyName: '',
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isDragging = useRef(false);

  const reset = useCallback(() => {
    setPhase('upload');
    setProgress(0);
    setPreview(null);
    setError('');
    setParsed({ firstName: '', lastName: '', email: '', phone: '', mobile: '', position: '', companyName: '' });
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const processImage = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Bitte eine Bilddatei auswählen (JPG, PNG, etc.)');
      return;
    }

    const url = URL.createObjectURL(file);
    setPreview(url);
    setPhase('scanning');
    setProgress(0);
    setError('');

    try {
      const result = await Tesseract.recognize(file, 'deu+eng', {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            setProgress(Math.round(m.progress * 100));
          }
        },
      });

      const text = result.data.text;
      const data = parseCardText(text);
      setParsed(data);
      setPhase('review');
    } catch (err) {
      setError(`OCR fehlgeschlagen: ${(err as Error).message}`);
      setPhase('upload');
    }
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) processImage(file);
    },
    [processImage],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      isDragging.current = false;
      const file = e.dataTransfer.files?.[0];
      if (file) processImage(file);
    },
    [processImage],
  );

  const handleConfirm = useCallback(() => {
    const result: Record<string, string> = {};
    if (parsed.firstName) result.firstName = parsed.firstName;
    if (parsed.lastName) result.lastName = parsed.lastName;
    if (parsed.email) result.email = parsed.email;
    if (parsed.phone) result.phone = parsed.phone;
    if (parsed.mobile) result.mobile = parsed.mobile;
    if (parsed.position) result.position = parsed.position;
    if (parsed.companyName) result.companyName = parsed.companyName;
    reset();
    onResult(result);
  }, [parsed, reset, onResult]);

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)', padding: '1rem',
      }}
      onClick={handleClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Visitenkarte scannen"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '520px',
          display: 'flex', flexDirection: 'column',
          background: 'var(--color-bg-card)',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.5rem',
          background: '#eff6ff', borderBottom: '1px solid #dbeafe',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1e3a8a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e3a8a' }}>
              Visitenkarte scannen
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            style={{
              width: 30, height: 30, fontSize: '1.375rem', lineHeight: 1,
              background: 'none', border: 'none', borderRadius: 'var(--radius-sm)',
              cursor: 'pointer', color: '#1e3a8a',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label="Schließen"
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.25rem 1.5rem' }}>

          {/* Error */}
          {error && (
            <div style={{
              padding: '0.625rem 0.875rem', marginBottom: '1rem',
              background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: 'var(--radius-sm)', color: 'var(--color-error)', fontSize: '0.875rem',
            }}>
              {error}
            </div>
          )}

          {/* PHASE: Upload */}
          {phase === 'upload' && (
            <>
              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); isDragging.current = true; }}
                onDragLeave={() => { isDragging.current = false; }}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '2.5rem 1rem',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, background 0.15s',
                  background: 'var(--color-bg)',
                  marginBottom: '1rem',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-primary)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-border)'; }}
              >
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 0.75rem' }}>
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <path d="M2 10h20" />
                </svg>
                <div style={{ fontWeight: 600, color: 'var(--color-text)', marginBottom: '0.25rem' }}>
                  Visitenkarte hochladen oder fotografieren
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                  Hierher ziehen oder klicken — JPG, PNG, HEIC
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => { if (fileInputRef.current) { fileInputRef.current.removeAttribute('capture'); fileInputRef.current.click(); } }}
                  style={{
                    flex: 1, padding: '0.5rem', fontSize: '0.875rem', fontWeight: 500,
                    background: 'var(--color-bg)', border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--color-text)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Bild auswählen
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    flex: 1, padding: '0.5rem', fontSize: '0.875rem', fontWeight: 500,
                    background: 'var(--color-primary)', border: 'none',
                    borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  Kamera öffnen
                </button>
              </div>
            </>
          )}

          {/* PHASE: Scanning */}
          {phase === 'scanning' && (
            <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
              {preview && (
                <img
                  src={preview}
                  alt="Visitenkarte"
                  style={{
                    maxHeight: 160, maxWidth: '100%', objectFit: 'contain',
                    borderRadius: 'var(--radius-sm)', marginBottom: '1.25rem',
                    border: '1px solid var(--color-border)',
                  }}
                />
              )}
              <div style={{ marginBottom: '0.75rem', fontWeight: 600, color: 'var(--color-text)' }}>
                Texterkennung läuft… {progress}%
              </div>
              <div style={{
                height: 6, background: 'var(--color-border)',
                borderRadius: 9999, overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%', background: 'var(--color-primary)',
                  width: `${progress}%`, transition: 'width 0.3s ease',
                  borderRadius: 9999,
                }} />
              </div>
              <div style={{ marginTop: '0.75rem', fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                Bitte warten — OCR analysiert das Bild
              </div>
            </div>
          )}

          {/* PHASE: Review */}
          {phase === 'review' && (
            <>
              {preview && (
                <img
                  src={preview}
                  alt="Visitenkarte"
                  style={{
                    maxHeight: 120, maxWidth: '100%', objectFit: 'contain',
                    borderRadius: 'var(--radius-sm)', marginBottom: '1rem',
                    border: '1px solid var(--color-border)', display: 'block',
                  }}
                />
              )}
              <p style={{ margin: '0 0 1rem', fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                Erkannte Felder prüfen und bei Bedarf anpassen:
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                {([
                  ['firstName', 'Vorname'],
                  ['lastName', 'Nachname'],
                  ['email', 'E-Mail'],
                  ['phone', 'Telefon'],
                  ['mobile', 'Mobil'],
                  ['position', 'Position'],
                ] as [keyof ParsedCard, string][]).map(([key, lbl]) => (
                  <div key={key}>
                    <label style={LABEL_STYLE}>{lbl}</label>
                    <input
                      style={INPUT_STYLE}
                      value={parsed[key]}
                      onChange={(e) => setParsed((p) => ({ ...p, [key]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={LABEL_STYLE}>Unternehmen</label>
                <input
                  style={INPUT_STYLE}
                  value={parsed.companyName}
                  onChange={(e) => setParsed((p) => ({ ...p, companyName: e.target.value }))}
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {phase !== 'scanning' && (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '0.875rem 1.5rem',
            borderTop: '1px solid var(--color-border)',
            gap: '0.75rem',
          }}>
            <button
              type="button"
              onClick={phase === 'review' ? reset : handleClose}
              style={{
                padding: '0.5rem 1.125rem', fontSize: '0.875rem',
                background: 'transparent', border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                color: 'var(--color-text-muted)',
              }}
            >
              {phase === 'review' ? '← Nochmal scannen' : 'Abbrechen'}
            </button>
            {phase === 'review' && (
              <button
                type="button"
                onClick={handleConfirm}
                style={{
                  padding: '0.5rem 1.25rem', fontSize: '0.875rem', fontWeight: 600,
                  background: 'var(--color-primary)', color: '#fff',
                  border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                }}
              >
                Kontakt anlegen →
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};
